import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, debounce } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    /* ------------TD CHANGE----------- */
    this.#calculateHeaderHeight();
    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('click', this.#onLinkClick);
    /* ----------END TD CHANGE--------- */
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    /* ------------TD CHANGE----------- */
    if (this.#handleResize?.cancel) {
      this.#handleResize.cancel();
    }
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('click', this.#onLinkClick);
    /* ----------END TD CHANGE--------- */
  }

  /* ------------TD CHANGE-----------
   * Mobile drawer: when an in-page hash link is clicked, close the drawer
   * first then scroll to the target. Without this, scroll-lock + the open
   * drawer prevent the browser's default anchor jump.
   */
  #onLinkClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor || !this.contains(anchor)) return;

    const href = anchor.getAttribute('href');
    if (!href || href === '#') return;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (!url.hash) return;
    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;

    const targetId = decodeURIComponent(url.hash.slice(1));
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    event.preventDefault();

    if (window.location.hash !== url.hash) {
      history.pushState(null, '', url.hash);
    }

    this.close();

    const scrollToTarget = () => {
      const headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--total-header-height'),
        10
      ) || 0;
      const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    };
    setTimeout(scrollToTarget, 250);
  };
  /* ----------END TD CHANGE--------- */
  /* ------------TD CHANGE----------- */
  #handleResize = debounce(() => {
    this.#calculateHeaderHeight();
  }, 100);

  #calculateHeaderHeight = () => {
    this.announcementBar = document.querySelector('.announcement-bar');
    this.header = document.querySelector('header');
    this.announcementBarHeight = this.announcementBar?.scrollHeight ?? 0;
    this.headerHeight = this.header?.scrollHeight ?? 0;
    this.totalHeaderHeight = this.announcementBarHeight + this.headerHeight - 1;
    document.documentElement.style.setProperty(
      '--total-header-height', // The name of your CSS variable
      `${this.totalHeaderHeight}px` // The value, including the 'px' unit
    );
  }

  /* ----------END TD CHANGE--------- */
  
  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Prevent native details element toggle behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    // Prevent native details element toggle behavior for submenus
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');

    // Only prevent accordion animations when opening the main drawer
    const isMainDrawer = details === this.refs.details;
    if (isMainDrawer) {
      this.preventInitialAccordionAnimations(details);
    }
    
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    /* ------------TD CHANGE----------- 
    This is to prevent the menu from getting stuck in Firefox.
    */
    setTimeout(() => {
    /* ----------END TD CHANGE--------- */
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    }, 200);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
